import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ContactHelper from "../../../../entities/contact/contact-helper";
import "./edit-contact-modal.scss";

const EditContactModal = ({ show, onHide, contact, onContactUpdated }) => {
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && contact) {
      setContactName(contact.name || "");
      setContactPhone(contact.phone && contact.phone !== "N/A" ? contact.phone : "");
      setContactEmail(contact.email && contact.email !== "N/A" ? contact.email : "");
      setError(null);
    }
  }, [show, contact]);

  useEffect(() => {
    if (!show) {
      setContactName("");
      setContactPhone("");
      setContactEmail("");
      setError(null);
    }
  }, [show]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!contactName.trim()) {
      setError("Please enter a name.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const updateData = { name: contactName.trim() };
      
      // Only include phone/email if they have values
      if (contactPhone.trim() && contactPhone.trim().toUpperCase() !== "N/A") {
        updateData.phone = contactPhone.trim();
      }
      if (contactEmail.trim() && contactEmail.trim().toUpperCase() !== "N/A") {
        updateData.email = contactEmail.trim();
      }
      
      await ContactHelper.updateContact(contact.id, updateData);
      onContactUpdated();
      onHide();
    } catch (err) {
      // Handle specific duplicate contact error
      if (err?.response?.status === 409) {
        setError(
          err?.response?.data?.message ||
          "A contact with this email or phone number already exists in your organization."
        );
      } else {
        setError(
          err?.response?.data?.message ||
            "Failed to update contact. Please try again."
        );
      }
    }
    setIsLoading(false);
  };

  if (!show) return null;

  return (
    <div
      className={`custom-modal-overlay ${show ? "show" : ""}`}
      role="dialog"
      aria-labelledby="editContactModalTitle"
      aria-hidden={!show}
    >
      <div className="custom-modal-content">
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="editContactModalTitle">
            Edit Contact
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onHide}
            aria-label="Close"
          ></button>
        </div>

        <div className="modal-body">
          <div className="modal-form-section">
            {error && <div className="alert alert-danger mb-3">{error}</div>}
            
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter contact name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  disabled={isLoading || (contact?.phone && contact.phone !== "N/A")}
                  placeholder={contact?.phone && contact.phone !== "N/A" ? "Phone number cannot be edited" : "Enter phone number"}
                  style={contact?.phone && contact.phone !== "N/A" ? { backgroundColor: '#f8f9fa', color: '#6c757d' } : {}}
                />
                <small className="text-muted">
                  {contact?.phone && contact.phone !== "N/A" 
                    ? "Phone number cannot be edited" 
                    : "Add phone number (optional)"
                  }
                </small>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  disabled={isLoading || (contact?.email && contact.email !== "N/A")}
                  placeholder={contact?.email && contact.email !== "N/A" ? "Email address cannot be edited" : "Enter email address"}
                  style={contact?.email && contact.email !== "N/A" ? { backgroundColor: '#f8f9fa', color: '#6c757d' } : {}}
                />
                <small className="text-muted">
                  {contact?.email && contact.email !== "N/A" 
                    ? "Email address cannot be edited" 
                    : "Add email address (optional)"
                  }
                </small>
              </div>
            </form>
          </div>
        </div>

        <div className="modal-footer justify-content-end">
          <button
            type="button"
            className="btn"
            onClick={onHide}
            disabled={isLoading}
          >
            <span>Close</span>
          </button>
          <button
            type="submit"
            className="bouncy-button small"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            <span>{isLoading ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

EditContactModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  contact: PropTypes.object,
  onContactUpdated: PropTypes.func.isRequired,
};

export default EditContactModal;